import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Wifi, WifiOff, Shield, Lock, AlertTriangle, CheckCircle,
  Clock, RefreshCw, Plus, Eye, EyeOff, Loader2, Activity,
  ExternalLink, Info, Key, LogOut
} from 'lucide-react';

// ─── Broker metadata ────────────────────────────────────────────────────────

type BrokerCategory = 'Crypto' | 'Forex' | 'Stocks' | 'Multi-Asset';

const CATEGORY_COLORS: Record<BrokerCategory, string> = {
  'Crypto': 'border-amber-400/20 text-amber-400 bg-amber-400/5',
  'Forex': 'border-sky-400/20 text-sky-400 bg-sky-400/5',
  'Stocks': 'border-emerald-400/20 text-emerald-400 bg-emerald-400/5',
  'Multi-Asset': 'border-violet-400/20 text-violet-400 bg-violet-400/5',
};

const BROKERS = [
  {
    id: 'interactive_brokers',
    name: 'Interactive Brokers',
    initials: 'IB',
    color: 'hsl(355, 70%, 50%)',
    logoUrl: 'https://logo.clearbit.com/interactivebrokers.com',
    category: 'Multi-Asset' as BrokerCategory,
    markets: ['Stocks', 'Options', 'Futures', 'Forex', 'Bonds'],
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Your IBKR API key', type: 'text' },
      { key: 'api_secret', label: 'API Secret', placeholder: 'Your IBKR API secret', type: 'password' },
    ],
    helpUrl: 'https://www.interactivebrokers.com/en/trading/ib-api.php',
    helpText: 'Get your API key from IBKR Client Portal',
    popular: true,
  },
  {
    id: 'binance',
    name: 'Binance',
    initials: 'BN',
    color: 'hsl(45, 100%, 51%)',
    logoUrl: 'https://logo.clearbit.com/binance.com',
    category: 'Crypto' as BrokerCategory,
    markets: ['Spot', 'Futures', 'Margin'],
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Your Binance API key', type: 'text' },
      { key: 'api_secret', label: 'Secret Key', placeholder: 'Your Binance secret key', type: 'password' },
    ],
    helpUrl: 'https://www.binance.com/en/my/settings/api-management',
    helpText: 'Create API key in Binance → API Management',
    popular: true,
  },
  {
    id: 'coinbase',
    name: 'Coinbase',
    initials: 'CB',
    color: 'hsl(216, 85%, 56%)',
    logoUrl: 'https://logo.clearbit.com/coinbase.com',
    category: 'Crypto' as BrokerCategory,
    markets: ['Spot', 'Advanced Trade'],
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Your Coinbase API key', type: 'text' },
      { key: 'api_secret', label: 'API Secret', placeholder: 'Your Coinbase API secret', type: 'password' },
    ],
    helpUrl: 'https://docs.cloud.coinbase.com/sign-in-with-coinbase/docs/api-key-authentication',
    helpText: 'Generate API key from Coinbase → Settings → API',
    popular: true,
  },
  {
    id: 'metatrader',
    name: 'MetaTrader',
    initials: 'MT',
    color: 'hsl(213, 60%, 45%)',
    logoUrl: 'https://logo.clearbit.com/metatrader5.com',
    category: 'Forex' as BrokerCategory,
    markets: ['Forex', 'CFDs', 'Metals'],
    fields: [
      { key: 'api_key', label: 'Account ID', placeholder: 'Your MT account number', type: 'text' },
      { key: 'api_secret', label: 'Password', placeholder: 'Your MT password', type: 'password' },
    ],
    helpUrl: 'https://www.metatrader5.com/en/terminal/help/startworking/acc_open',
    helpText: 'Use your MetaTrader account credentials',
    popular: false,
  },
  {
    id: 'oanda',
    name: 'OANDA',
    initials: 'OA',
    color: 'hsl(0, 0%, 20%)',
    logoUrl: 'https://logo.clearbit.com/oanda.com',
    category: 'Forex' as BrokerCategory,
    markets: ['Forex', 'CFDs', 'Metals'],
    fields: [
      { key: 'api_key', label: 'API Token', placeholder: 'Your OANDA API token', type: 'password' },
      { key: 'api_secret', label: 'Account ID', placeholder: 'Your OANDA account ID', type: 'text' },
    ],
    helpUrl: 'https://developer.oanda.com/rest-live-v20/introduction/',
    helpText: 'Generate API token from OANDA → Manage API Access',
    popular: false,
  },
  {
    id: 'kraken',
    name: 'Kraken',
    initials: 'KR',
    color: 'hsl(260, 60%, 50%)',
    logoUrl: 'https://logo.clearbit.com/kraken.com',
    category: 'Crypto' as BrokerCategory,
    markets: ['Spot', 'Futures', 'Margin', 'Staking'],
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Your Kraken API key', type: 'text' },
      { key: 'api_secret', label: 'Private Key', placeholder: 'Your Kraken private key', type: 'password' },
    ],
    helpUrl: 'https://support.kraken.com/hc/en-us/articles/360000919966',
    helpText: 'Create API key from Kraken → Settings → API',
    popular: true,
  },
  {
    id: 'bybit',
    name: 'Bybit',
    initials: 'BB',
    color: 'hsl(36, 100%, 50%)',
    logoUrl: 'https://logo.clearbit.com/bybit.com',
    category: 'Crypto' as BrokerCategory,
    markets: ['Spot', 'Derivatives', 'Copy Trading'],
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Your Bybit API key', type: 'text' },
      { key: 'api_secret', label: 'API Secret', placeholder: 'Your Bybit API secret', type: 'password' },
    ],
    helpUrl: 'https://www.bybit.com/app/user/api-management',
    helpText: 'Create API key from Bybit → API Management',
    popular: false,
  },
  {
    id: 'alpaca',
    name: 'Alpaca',
    initials: 'AL',
    color: 'hsl(48, 95%, 52%)',
    logoUrl: 'https://logo.clearbit.com/alpaca.markets',
    category: 'Stocks' as BrokerCategory,
    markets: ['Stocks', 'ETFs', 'Crypto'],
    fields: [
      { key: 'api_key', label: 'API Key ID', placeholder: 'Your Alpaca key ID', type: 'text' },
      { key: 'api_secret', label: 'Secret Key', placeholder: 'Your Alpaca secret key', type: 'password' },
    ],
    helpUrl: 'https://alpaca.markets/docs/trading/getting_started/',
    helpText: 'Get API keys from Alpaca → Paper or Live dashboard',
    popular: false,
  },
  {
    id: 'kucoin',
    name: 'KuCoin',
    initials: 'KC',
    color: 'hsl(160, 75%, 42%)',
    logoUrl: 'https://logo.clearbit.com/kucoin.com',
    category: 'Crypto' as BrokerCategory,
    markets: ['Spot', 'Futures', 'Margin'],
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Your KuCoin API key', type: 'text' },
      { key: 'api_secret', label: 'API Secret', placeholder: 'Your KuCoin API secret', type: 'password' },
    ],
    helpUrl: 'https://www.kucoin.com/account/api',
    helpText: 'Create API key from KuCoin → API Management',
    popular: false,
  },
  // ── INDIAN BROKERS ──────────────────────────────────────────────
  {
    id: 'zerodha',
    name: 'Zerodha',
    initials: 'ZE',
    color: 'hsl(220, 70%, 50%)',
    logoUrl: 'https://logo.clearbit.com/zerodha.com',
    category: 'Stocks' as BrokerCategory,
    markets: ['Equity', 'F&O', 'Currency', 'Commodity'],
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Your Kite API key', type: 'text' },
      { key: 'api_secret', label: 'API Secret', placeholder: 'Your Kite API secret', type: 'password' },
    ],
    helpUrl: 'https://kite.trade/docs/connect/v3/',
    helpText: 'Get API key from kite.trade → My Apps',
    popular: true,
  },
  {
    id: 'groww',
    name: 'Groww',
    initials: 'GW',
    color: 'hsl(160, 75%, 40%)',
    logoUrl: 'https://logo.clearbit.com/groww.in',
    category: 'Stocks' as BrokerCategory,
    markets: ['Equity', 'MF', 'US Stocks', 'F&O'],
    fields: [
      { key: 'api_key', label: 'API Token', placeholder: 'Your Groww API token', type: 'password' },
    ],
    helpUrl: 'https://groww.in/open-account',
    helpText: 'Groww Trade API — token from developer portal',
    popular: true,
  },
  {
    id: 'dhan',
    name: 'Dhan',
    initials: 'DH',
    color: 'hsl(200, 80%, 45%)',
    logoUrl: 'https://logo.clearbit.com/dhan.co',
    category: 'Stocks' as BrokerCategory,
    markets: ['Equity', 'F&O', 'Currency'],
    fields: [
      { key: 'api_key', label: 'Client ID', placeholder: 'Your Dhan client ID', type: 'text' },
      { key: 'api_secret', label: 'Access Token', placeholder: 'Your Dhan access token', type: 'password' },
    ],
    helpUrl: 'https://dhanhq.co/docs/v2/',
    helpText: 'Get access token from Dhan → API section',
    popular: true,
  },
  {
    id: 'upstox',
    name: 'Upstox',
    initials: 'UP',
    color: 'hsl(270, 60%, 50%)',
    logoUrl: 'https://logo.clearbit.com/upstox.com',
    category: 'Stocks' as BrokerCategory,
    markets: ['Equity', 'F&O', 'Currency'],
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Upstox API key', type: 'text' },
      { key: 'api_secret', label: 'API Secret', placeholder: 'Upstox API secret', type: 'password' },
    ],
    helpUrl: 'https://upstox.com/developer/api-documentation/',
    helpText: 'Register your app at upstox.com/developer',
    popular: false,
  },
  {
    id: 'angelone',
    name: 'Angel One',
    initials: 'AO',
    color: 'hsl(4, 80%, 55%)',
    logoUrl: 'https://logo.clearbit.com/angelone.in',
    category: 'Stocks' as BrokerCategory,
    markets: ['Equity', 'F&O', 'Mutual Funds'],
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Angel One SmartAPI key', type: 'text' },
      { key: 'api_secret', label: 'Client Code', placeholder: 'Your client code', type: 'text' },
    ],
    helpUrl: 'https://smartapi.angelone.in/docs',
    helpText: 'Get API key from SmartAPI developer portal',
    popular: false,
  },
];

// ─── Types ──────────────────────────────────────────────────────────────────

interface BrokerConnection {
  id: string;
  broker_name: string;
  broker_user_id: string | null;
  status: 'connected' | 'disconnected' | 'error' | 'expired';
  last_verified_at: string | null;
  token_expires_at: string | null;
  created_at: string;
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

const StatusPill = ({ status }: { status: string }) => {
  const config = {
    connected:    { color: 'text-emerald-400', bg: 'bg-emerald-400/10', dot: 'bg-emerald-400', label: 'Connected', pulse: true },
    disconnected: { color: 'text-muted-foreground', bg: 'bg-muted/20', dot: 'bg-muted-foreground', label: 'Disconnected', pulse: false },
    expired:      { color: 'text-amber-400', bg: 'bg-amber-400/10', dot: 'bg-amber-400', label: 'Token Expired', pulse: false },
    error:        { color: 'text-destructive', bg: 'bg-destructive/10', dot: 'bg-destructive', label: 'Error', pulse: false },
  }[status] || { color: 'text-muted-foreground', bg: 'bg-muted/20', dot: 'bg-muted-foreground', label: status, pulse: false };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${config.bg} ${config.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot} ${config.pulse ? 'animate-pulse' : ''}`} />
      {config.label}
    </span>
  );
};

const BrokerLogo = ({ broker }: { broker: typeof BROKERS[0] }) => {
  const [imgError, setImgError] = React.useState(false);

  return (
    <div
      className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-muted/30 border border-border/20 overflow-hidden"
    >
      {broker.logoUrl && !imgError ? (
        <img
          src={broker.logoUrl}
          alt={`${broker.name} logo`}
          className="w-7 h-7 object-contain"
          onError={() => setImgError(true)}
          loading="lazy"
        />
      ) : (
        <span
          className="text-xs font-bold text-white"
          style={{ color: broker.color }}
        >
          {broker.initials}
        </span>
      )}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const BrokerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [connections, setConnections] = useState<Record<string, BrokerConnection>>({});
  const [loading, setLoading] = useState(true);
  const [connectingBroker, setConnectingBroker] = useState<typeof BROKERS[0] | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showFields, setShowFields] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);

  // ── Fetch real connections from broker_connections table ──
  const fetchConnections = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('broker_connections')
      .select('id, broker_name, broker_user_id, status, last_verified_at, token_expires_at, created_at')
      .eq('user_id', user.id);

    if (error) {
      console.error('fetchConnections error:', error);
      return;
    }
    const map: Record<string, BrokerConnection> = {};
    (data || []).forEach(row => { map[row.broker_name] = row as BrokerConnection; });
    setConnections(map);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  // ── Realtime subscription so status updates instantly ──
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('broker-connections-realtime')
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'broker_connections',
        filter: `user_id=eq.${user.id}`
      }, () => fetchConnections())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, fetchConnections]);

  // ── Connect flow ──
  const handleOpenConnect = (broker: typeof BROKERS[0]) => {
    setFormData({});
    setShowFields({});
    setConnectingBroker(broker);
  };

  const handleSubmitConnect = async () => {
    if (!connectingBroker || !user) return;

    const missing = connectingBroker.fields.filter(f => !formData[f.key]?.trim());
    if (missing.length > 0) {
      toast({ title: 'Missing fields', description: `Please fill in: ${missing.map(f => f.label).join(', ')}`, variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-broker-connection`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.session.access_token}`,
          },
          body: JSON.stringify({
            broker_name: connectingBroker.id,
            api_key: formData['api_key'] || formData[connectingBroker.fields[0]?.key] || '',
            api_secret: formData['api_secret'] || formData[connectingBroker.fields[1]?.key] || '',
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Connection failed');
      }

      toast({
        title: `${connectingBroker.name} connected`,
        description: `Credentials verified and encrypted. Broker ID: ${result.broker_user_id || 'confirmed'}`,
      });

      setConnectingBroker(null);
      setFormData({});
      fetchConnections();
    } catch (err: any) {
      toast({ title: 'Connection failed', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Disconnect flow ──
  const handleDisconnect = async (brokerName: string) => {
    if (!user) return;
    setDisconnecting(brokerName);
    try {
      const { error } = await supabase
        .from('broker_connections')
        .update({ status: 'disconnected', updated_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('broker_name', brokerName);

      if (error) throw error;

      toast({ title: 'Broker disconnected', description: `${brokerName} has been disconnected. Strategies will use demo mode.` });
      fetchConnections();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDisconnecting(null);
    }
  };

  const handleReconnect = (broker: typeof BROKERS[0]) => {
    handleOpenConnect(broker);
  };

  // ── Derived stats ──
  const connectedList = Object.values(connections).filter(c => c.status === 'connected');
  const expiredList = Object.values(connections).filter(c => c.status === 'expired');
  const connectedCount = connectedList.length;

  const formatVerified = (iso: string | null) => {
    if (!iso) return 'Never';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const isTokenExpiringSoon = (iso: string | null) => {
    if (!iso) return false;
    return new Date(iso).getTime() - Date.now() < 6 * 3600 * 1000;
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <TooltipProvider>
      <div className="space-y-6">

        {/* ── Header stats bar ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Connected', value: connectedCount, sub: `of ${BROKERS.length} brokers`, icon: Wifi, color: connectedCount > 0 ? 'text-emerald-400' : 'text-muted-foreground' },
            { label: 'Strategies Protected', value: connectedCount > 0 ? 'Live' : 'Demo', sub: connectedCount > 0 ? 'Real broker execution' : 'Mock execution', icon: Activity, color: connectedCount > 0 ? 'text-emerald-400' : 'text-amber-400' },
            { label: 'Encryption', value: 'AES-256', sub: 'Credentials at rest', icon: Lock, color: 'text-primary' },
            { label: 'Attention Needed', value: expiredList.length, sub: expiredList.length > 0 ? 'Token(s) expired' : 'All tokens valid', icon: AlertTriangle, color: expiredList.length > 0 ? 'text-amber-400' : 'text-emerald-400' },
          ].map((stat) => (
            <Card key={stat.label} className="bg-card/40 border-border/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <stat.icon className={`w-4 h-4 mt-0.5 shrink-0 ${stat.color}`} />
                  <div className="min-w-0">
                    <p className="text-[11px] text-muted-foreground">{stat.label}</p>
                    <p className="text-lg font-bold leading-tight">{stat.value}</p>
                    <p className="text-[10px] text-muted-foreground/70 truncate">{stat.sub}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── Security notice ── */}
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/10">
          <Shield className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">Bank-grade security.</span>{' '}
            Your API keys are encrypted with AES-256 (pgcrypto) before storage.
            Plaintext credentials never touch our database. Decryption happens only
            inside isolated backend functions during trade execution.
          </p>
        </div>

        {/* ── Expired token warning ── */}
        <AnimatePresence>
          {expiredList.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-400/5 border border-amber-400/15"
            >
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-300 leading-relaxed">
                {expiredList.map(e => e.broker_name).join(', ')} token{expiredList.length > 1 ? 's have' : ' has'} expired.
                Affected strategies are running in demo mode until you reconnect.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Broker cards grid ── */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BROKERS.map(b => (
              <Skeleton key={b.id} className="h-48 rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BROKERS.map((broker, idx) => {
              const conn = connections[broker.id];
              const status = conn?.status || 'disconnected';
              const isConnected = status === 'connected';
              const isExpired = status === 'expired';
              const isError = status === 'error';
              const expiringSoon = isConnected && isTokenExpiringSoon(conn?.token_expires_at ?? null);

              return (
                <motion.div
                  key={broker.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Card className={`bg-card/40 border-border/30 rounded-xl transition-colors press-scale min-h-[180px] ${
                    isConnected ? 'border-emerald-500/20' : isExpired ? 'border-amber-400/20' : ''
                  }`}>
                    <CardContent className="p-5 space-y-4">

                      {/* Card header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <BrokerLogo broker={broker} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-semibold">{broker.name}</h3>
                              <Badge variant="outline" className={`text-[9px] h-4 font-normal ${CATEGORY_COLORS[broker.category]}`}>
                                {broker.category}
                              </Badge>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              {isConnected
                                ? `Verified ${formatVerified(conn.last_verified_at)}`
                                : isExpired
                                ? 'Token expired — reconnect required'
                                : 'Not connected'}
                            </p>
                          </div>
                        </div>
                        <StatusPill status={status} />
                      </div>

                      {/* Markets */}
                      <div className="flex flex-wrap gap-1">
                        {broker.markets.map(m => (
                          <Badge key={m} variant="outline" className="text-[10px] h-5 border-border/30 text-muted-foreground font-normal">
                            {m}
                          </Badge>
                        ))}
                      </div>

                      {/* Connected details */}
                      {isConnected && conn.broker_user_id && (
                        <div className="flex items-center justify-between text-[11px] px-3 py-2 rounded-lg bg-muted/10 border border-border/10">
                          <span className="text-muted-foreground">Broker ID</span>
                          <span className="flex items-center gap-1.5 font-mono text-foreground">
                            <Key className="w-3 h-3 text-muted-foreground" />
                            {conn.broker_user_id.slice(0, 12)}••••
                          </span>
                        </div>
                      )}

                      {/* Expiring soon warning */}
                      {expiringSoon && (
                        <div className="flex items-center gap-2 text-[11px] text-amber-400 px-3 py-2 rounded-lg bg-amber-400/5">
                          <Clock className="w-3.5 h-3.5 shrink-0" />
                          Token expires within 6 hours — reconnect to avoid interruption
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex items-center gap-2">
                        {isConnected ? (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => handleReconnect(broker)}>
                                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Rotate Keys
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Update API credentials</TooltipContent>
                            </Tooltip>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                              onClick={() => handleDisconnect(broker.id)}
                              disabled={disconnecting === broker.id}
                            >
                              {disconnecting === broker.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <LogOut className="w-3.5 h-3.5" />}
                            </Button>
                          </>
                        ) : isExpired || isError ? (
                          <Button className="w-full h-8 text-xs" onClick={() => handleReconnect(broker)}>
                            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reconnect
                          </Button>
                        ) : (
                          <Button className="w-full h-8 text-xs" onClick={() => handleOpenConnect(broker)}>
                            <Plus className="w-3.5 h-3.5 mr-1.5" /> Connect
                          </Button>
                        )}
                      </div>

                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* ── Request broker card ── */}
        <Card className="border-dashed border-2 border-border/20 bg-card/20">
          <CardContent className="p-5 text-center">
            <Plus className="w-6 h-6 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm font-medium mb-1">Need another broker?</p>
            <p className="text-xs text-muted-foreground mb-3">We add brokers based on demand.</p>
            <Button variant="outline" size="sm" className="h-7 text-xs">Request Broker</Button>
          </CardContent>
        </Card>
      </div>

      {/* ── Connect modal ── */}
      <AnimatePresence>
        {connectingBroker && (
          <Dialog open={!!connectingBroker} onOpenChange={(open) => { if (!open && !submitting) setConnectingBroker(null); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <BrokerLogo broker={connectingBroker} />
                  <div>
                    <DialogTitle>Connect {connectingBroker.name}</DialogTitle>
                    <DialogDescription className="text-xs mt-1">
                      {connectingBroker.helpText}
                    </DialogDescription>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Your credentials are encrypted before storage. We never see your plaintext keys.
                </p>
              </DialogHeader>

              <div className="space-y-4 pt-2">

                {/* Security badge */}
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-primary/5 border border-primary/10">
                  <Lock className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    <span className="font-medium text-foreground">AES-256 encrypted</span> — credentials are encrypted
                    inside our backend function before any database write.
                    Rate limited to 5 attempts per hour.
                  </p>
                </div>

                {/* Credential fields */}
                {connectingBroker.fields.map(field => (
                  <div key={field.key} className="space-y-1.5">
                    <Label className="text-xs">{field.label}</Label>
                    <div className="relative">
                      <Input
                        type={field.type === 'password' && !showFields[field.key] ? 'password' : 'text'}
                        placeholder={field.placeholder}
                        value={formData[field.key] || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        className="pr-10"
                        autoComplete="off"
                        spellCheck={false}
                      />
                      {field.type === 'password' && (
                        <button
                          type="button"
                          onClick={() => setShowFields(prev => ({ ...prev, [field.key]: !prev[field.key] }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showFields[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Help link */}
                <a
                  href={connectingBroker.helpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Where do I find my {connectingBroker.name} API credentials?
                </a>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setConnectingBroker(null)}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button className="flex-1" onClick={handleSubmitConnect} disabled={submitting}>
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Verifying…</>
                    ) : (
                      <><Shield className="w-4 h-4 mr-1.5" /> Connect Securely</>
                    )}
                  </Button>
                </div>

              </div>
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>
    </TooltipProvider>
  );
};

export default BrokerDashboard;
